import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Users, Heart, Microscope, Sprout } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const ResearchPageNew = () => {
  const researchProjects = [
    {
      id: 1,
      title: 'Ekologos',
      subtitle: 'Global Environmental Humanities Network',
      description: 'A collaborative project funded by the Norwegian Agency bridging indigenous knowledge systems with contemporary climate science across Arctic, Amazonian, and Himalayan regions.',
      icon: Globe,
      color: 'green',
      slug: 'ekologos',
      image: 'https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/kp2xwc0c_SAS%20Environmental%20Studies.jpeg'
    },
    {
      id: 2,
      title: 'MyCClimate',
      subtitle: 'Community Responses to Climate Change',
      description: 'Ethnographic research along the India-Myanmar border studying local perceptions and adaptation strategies to climate change in Highland Asia.',
      icon: Users,
      color: 'blue',
      slug: 'myclimate',
      image: 'https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/373jtuu5_SAS%20Anthropology.jpg'
    },
    {
      id: 3,
      title: 'PluriMed',
      subtitle: 'Medical Pluralism in Highland Asia',
      description: 'Advancing critical inquiry into therapeutic ecologies and the intersections of formal and informal health knowledge systems across highland communities.',
      icon: Microscope,
      color: 'purple',
      slug: 'plurimed',
      image: 'https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/19qqt0ph_SAS%20Health%20Sciences.JPG'
    },
    {
      id: 4,
      title: 'Earthkeepers',
      subtitle: 'Environmental Education & Climate Resilience',
      description: 'Community-driven environmental education initiative empowering children and elders through storytelling and traditional knowledge exchange.',
      icon: Sprout,
      color: 'teal',
      slug: 'earthkeepers',
      image: 'https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/ww4wtg1g_SAS%20Philosophy.JPG'
    }
  ];

  const coreThemes = [
    {
      title: 'Societies and Cultures of Highland Asia',
      description: 'Oral traditions, kinship, religious change, customary law, and ancestral knowledge systems',
      focus: ['Nagaland Oral Literature Project', 'Intersubjective Ecologies Lab', 'Summer and Winter Schools']
    },
    {
      title: 'Environmental Knowledge and Climate Resilience',
      description: 'Indigenous ecological knowledge, traditional land management, and climate adaptation strategies',
      focus: ['Earthkeepers', 'MyCClimate', 'Citizen Science Biodiversity Studies']
    },
    {
      title: 'Religion, Healing, and Medical Pluralism',
      description: 'Shamanism, prophetic healing, medical pluralism, and rural healthcare ecologies',
      focus: ['PluriMed Research Group', 'Collaboration with Doctors For You', 'Ekologos Fellowship Programme']
    },
    {
      title: 'Political Economy, Borders, and Mobility',
      description: 'Indigenous sovereignty, cross-border trade, migration, and spatial politics of belonging',
      focus: ['India–Myanmar Borderlands Research', 'Refugee & Diaspora Studies', 'Conflict & Memory Archives']
    },
    {
      title: 'Visual, Material, and Museum Anthropology',
      description: 'Collaborative exhibitions, repatriation ethics, ethnographic filmmaking, and digital archives',
      focus: ['Museum Collaborations (Pitt Rivers, Cambridge MAA)', 'Visual Anthropology Labs', 'Oral History Projects']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Research Projects
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              Locally rooted, globally engaged scholarship addressing the urgencies of our time
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Our Research Approach</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-center mb-6">
                The Highland Institute's work is grounded in deep, long-term ethnographic engagement with highland
                communities. Our research is organized around five core themes, each reflecting our interdisciplinary
                orientation and commitment to addressing both scholarly and real-world challenges.
              </p>
              <p className="text-gray-700 leading-relaxed text-center">
                These themes emerge from over a decade of collaboration with communities across Highland Asia and
                beyond, animated by questions of identity, ecology, healing, mobility, and power.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Featured Research Projects</h2>
            <div className="h-1 w-24 bg-green-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {researchProjects.map((project) => {
              const IconComponent = project.icon;
              return (
                <Card key={project.id} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className={`w-12 h-12 bg-${project.color}-100 rounded-full flex items-center justify-center mb-3`}>
                        <IconComponent className={`w-6 h-6 text-${project.color}-600`} />
                      </div>
                      <h3 className="text-2xl font-bold text-white">{project.title}</h3>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-lg text-green-600">{project.subtitle}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-6">{project.description}</p>
                    <Link to={`/research/${project.slug}`}>
                      <Button className="w-full bg-green-600 hover:bg-green-700 group-hover:scale-105 transition-transform">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Research Themes */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12 text-center">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Core Themes of Research</h2>
              <div className="h-1 w-24 bg-green-600 mx-auto"></div>
            </div>

            <div className="space-y-8">
              {coreThemes.map((theme, index) => (
                <Card key={index} className="hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-2xl text-gray-900">{theme.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4 leading-relaxed">{theme.description}</p>
                    <div className="border-l-4 border-green-600 pl-4 bg-gray-50 p-4 rounded-r-lg">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Projects & Activities:</p>
                      <ul className="space-y-1">
                        {theme.focus.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-600 flex items-center">
                            <ArrowRight className="w-3 h-3 text-green-600 mr-2 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Additional Research Areas */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Additional Research Initiatives</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>Tasting Tomorrow</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Applied research investigating how cultural heritage and cuisine can adapt to climate change
                    through experimental cooking with future-available ingredients.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>Citizen Science & Biodiversity</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Community-led biodiversity mapping, participatory conservation initiatives, and integration
                    of Traditional Ecological Knowledge (TEK).
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>Ursula Graham Bower Lectures</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Annual lecture series featuring international scholars exploring historical, political, and
                    cultural dimensions of Highland Asia.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>Highlander Press Journals</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Open access publications including <em>PluriMed</em> journal and academic monographs
                    disseminating research from the region.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Collaborate With Us</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Interested in partnering on research, accessing our publications, or joining our fellowship programmes?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/programmes">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Study With Us
              </Button>
            </Link>
            <a href="mailto:info@highlandinstitute.org">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Researchers
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchPageNew;
